<script lang="ts" setup>
const { t: $t } = useI18n()

import { joinURL, withQuery } from 'ufo'
import type { CreateQuickSale } from '~/types'

defineProps<{
  machineryId: string
}>()

const { quickSale } = useQuery()
const { data: lastUsedTemplate } = quickSale.lastUsedTemplate()

// Mutate data
const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()
const createQuickSale = useMutation({
  mutationFn: $trpc.quickSale.create.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.machinery.details.createQuickSale.notification.error') }),
  onSuccess: async (quickSale) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['machines'] }),
      queryClient.invalidateQueries({ queryKey: ['quickSales'] }),
    ])
    quickSalePayload.value = null

    const url = withQuery(
      joinURL('/api/pdf/quickSale/details/', quickSale.id),
      { language: quickSalePDFLanguage.value },
    )
    window.open(url, '_blank')

    notification.success({ title: $t('customerModule.machinery.details.createQuickSale.notification.success'), duration: 4500 })
  },
})

const quickSalePayload = ref<CreateQuickSale | null>(null)
const quickSalePDFLanguage = ref<string>('de')
function saveQuickSale(data: CreateQuickSale & { language: string }) {
  const { language, ...createData } = data
  quickSalePDFLanguage.value = language
  return createQuickSale.mutate(createData)
}
</script>

<template>
  <div class="flex flex-col gap-4 lg:col-span-7 xl:col-span-5">
    <CustomerModuleMachineryDetailsPartnerSalesQuickSales
      :machinery-id
      @create-quick-sale="data => quickSalePayload = {
        ...data,
        templateFile: lastUsedTemplate?.templateFilePath ? { path: lastUsedTemplate.templateFilePath } : undefined,
      }"
    />
  </div>

  <ThePopup
    :show="Boolean(quickSalePayload)"
    :title="$t('customerModule.machinery.details.partnerSales.createQuickSale.title')"
    @close="quickSalePayload = null"
  >
    <QuickSaleCreationForm v-if="quickSalePayload" :payload="quickSalePayload" @save="saveQuickSale" />
  </ThePopup>
</template>
